// 인스타 광고 유입 파라미터(lp/utm) 파싱·보존.
//  광고 URL 예: https://muyeon.co.kr/?lp=teacher&utm_source=instagram&utm_campaign=teacher_0804
//  - lp = 유입 유형(teacher|academy|student) → 유형별 "웹에서 완주 가능한 다음 단계" CTA 유도.
//  - utm_* = 마케팅 기여 추적용. 가입 후에도 참조할 수 있게 localStorage 에 보존.
//  consent.js 의 저장 헬퍼 스타일(try/catch storage)과 동일 패턴.

// lp 유효값 (백엔드 계약 아님 — 프론트 유도 분기용)
//  teacher_sub = 강사 광고 → 대타공고 등록(/subs/create), academy_job = 학원 광고 → 채용공고 등록(/jobs/create).
//  이 두 유형은 광고 링크가 곧바로 작성 화면을 열므로 1회 안내 팝업(LandingCtaHost)을 띄우지 않는다.
//  ★ 2026-10-21 원장 광고는 /hire?lp=academy 선택 화면 → 채용(academy_job) / 대타(academy_sub).
export const LANDING_TYPES = ["teacher", "academy", "student", "teacher_sub", "academy_job", "academy_sub"];
// 공고 작성 광고 — 작성 화면 상단 안내·선택 칸 접기·광고 등록 경로를 쓴다.
export const POSTING_AD_TYPES = ["teacher_sub", "academy_job", "academy_sub"];

const LP_KEY = "muyeon.lp";            // 유입 유형(teacher|academy|student)
const UTM_KEY = "muyeon.lp.utm";       // utm 파라미터 묶음(JSON)
const PENDING_KEY = "muyeon.lp.pending"; // 아직 CTA 안내를 안 띄운 신규 유입이면 '1'

let captured = false; // 한 페이지 로드당 1회만 파싱

const isValidType = (v) => LANDING_TYPES.includes(v);

/**
 * 첫 진입 시 window.location.search 에서 lp/utm 을 읽어 저장.
 *  - 렌더 중(효과 전) 동기 호출 가능 → Start 의 /home replace 로 쿼리가 사라지기 전에 캡처.
 *  - 유효한 lp 가 있으면 PENDING 을 세워 CTA 1회 노출 대상으로 표시.
 *  - lp 없이 utm 만 있어도 utm 은 보존(기여 추적).
 */
export function captureLandingParams() {
  if (captured) return;
  captured = true;
  try {
    const params = new URLSearchParams(window.location.search || "");
    const lp = (params.get("lp") || "").toLowerCase().trim();

    const utm = {};
    params.forEach((value, key) => {
      if (/^utm_/i.test(key)) utm[key.toLowerCase()] = value;
    });
    if (Object.keys(utm).length) {
      try { localStorage.setItem(UTM_KEY, JSON.stringify(utm)); } catch (e) { /* noop */ }
    }

    if (isValidType(lp)) {
      localStorage.setItem(LP_KEY, lp);
      localStorage.setItem(PENDING_KEY, "1"); // 신규 광고 클릭 → CTA 노출 대기
    }
  } catch (e) { /* noop */ }
}

// 저장된 유입 유형(없으면 null)
export function getLandingType() {
  try {
    const v = localStorage.getItem(LP_KEY);
    return isValidType(v) ? v : null;
  } catch (e) { return null; }
}

/** 광고 선택 화면(/hire)에서 고른 공고 종류로 유입 유형을 바꾼다(같은 페이지 안 이동이라 URL 을 다시 읽지 않는다). */
export function setLandingType(lp) {
  if (!isValidType(lp)) return;
  try { localStorage.setItem(LP_KEY, lp); } catch (e) { /* noop */ }
}

/** 지금 유입이 공고 작성 광고(대타·채용)인가. */
export const isPostingAd = () => POSTING_AD_TYPES.includes(getLandingType());
/** 광고 퍼널을 세는 유입 — 공고 광고 + 회원 레슨 요청 광고(lp=student, 10-24) + 강사 일자리 광고(lp=teacher, 11-02). */
export const isPaidAd = () => isPostingAd() || ["student", "teacher"].includes(getLandingType());

// CTA 안내를 아직 안 띄운 신규 유입인가
export function hasPendingLandingCta() {
  try { return localStorage.getItem(PENDING_KEY) === "1" && !!getLandingType(); }
  catch (e) { return false; }
}

// CTA 안내를 띄웠음(1회 소비) — lp/utm 값은 기여 추적용으로 유지, pending 만 해제
export function consumeLandingCta() {
  try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* noop */ }
}

// 보존된 utm 묶음(없으면 {})
export function getLandingUtm() {
  try { return JSON.parse(localStorage.getItem(UTM_KEY) || "{}") || {}; }
  catch (e) { return {}; }
}

// 서버에 남기는 광고 유입 기록(전환 측정) — 인증 대기 공고 생성 body 의 landing.
//  { lp, utm_source, utm_medium, utm_campaign, utm_content } 중 값이 있는 것만. 없으면 undefined.
const LANDING_UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_id"];
export function getLandingPayload() {
  const out = {};
  const lp = getLandingType();
  if (lp) out.lp = lp;
  const utm = getLandingUtm();
  LANDING_UTM_KEYS.forEach((k) => {
    if (typeof utm[k] === "string" && utm[k]) out[k] = utm[k].slice(0, 100);
  });
  return Object.keys(out).length ? out : undefined;
}
